import React, { createContext, CSSProperties, ReactNode } from 'react';
import { Link } from 'react-router-dom';

import {
  ArrowRightOutlined,
  FormOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import { Modal, Typography } from 'antd';

import { BuildingOutlined, ClinicOutlined } from './MabelIcons';

export const ClientSetupGuideContext = createContext<{
  openSetupGuide: () => void;
}>({
  openSetupGuide: () => undefined,
});

type SetupStep = {
  description: string;
  href?: string;
  icon?: ReactNode;
  linkText: string;
  path?: string;
  title: string;
};

const stepRowStyle: CSSProperties = {
  borderTop: '1px solid #f0e6ec',
  display: 'flex',
  gap: 12,
  padding: '12px 0',
};

const stepNumberStyle: CSSProperties = {
  alignItems: 'center',
  background: '#870051',
  borderRadius: 12,
  color: '#ffffff',
  display: 'flex',
  flex: '0 0 auto',
  fontSize: 13,
  fontWeight: 600,
  height: 24,
  justifyContent: 'center',
  width: 24,
};

const stepLinkIconStyle: CSSProperties = {
  marginRight: 4,
};

const getSetupSteps = (organizationName: string): SetupStep[] => [
  {
    description:
      "Add the CMS participants list file for your network. You'll then be prompted to create new practices that match the TINs/CCNs in the list.",
    icon: <UploadOutlined />,
    linkText: 'File Uploads',
    path: '/file-uploads',
    title: 'Upload your participant list and create corresponding practices',
  },
  {
    description:
      'Update practice names, add addresses, logos, and language preferences.',
    icon: <ClinicOutlined />,
    linkText: 'Practices',
    path: '/client-practices',
    title: 'Complete practice details',
  },
  {
    description: 'Update display name, logos, etc. for each ACO Entity.',
    icon: <BuildingOutlined />,
    linkText: 'ACO Entities',
    path: '/aco-entities',
    title: 'Complete ACO-level details',
  },
  {
    description:
      'Get the SVA packets for your network from the Forms page and distribute them to practices.',
    icon: <FormOutlined />,
    linkText: 'Forms',
    path: '/template-previews',
    title: 'Download SVA packets',
  },
  {
    description: 'Let the Mabel team know once your setup is complete.',
    href: `mailto:support@getmabel.com?subject=${encodeURIComponent(
      `Network setup complete — ${organizationName}`
    )}&body=${encodeURIComponent(
      `Hi Mabel team,\n\nWe have completed the setup steps for ${organizationName}.\n\nThanks!`
    )}`,
    linkText: 'Email support@getmabel.com',
    title: 'Notify the Mabel team',
  },
];

const ClientSetupGuideModal: React.FC<{
  onClose: () => void;
  organizationName: string;
  visible: boolean;
}> = ({ onClose, organizationName, visible }) => (
  <Modal
    footer={null}
    onCancel={onClose}
    title={
      <Typography.Title level={4} style={{ margin: 0 }}>
        How to set up your organization
      </Typography.Title>
    }
    visible={visible}
    width={640}
  >
    {getSetupSteps(organizationName).map((step, index) => (
      <div
        key={step.title}
        style={{
          ...stepRowStyle,
          borderTop: index === 0 ? undefined : stepRowStyle.borderTop,
        }}
      >
        <div style={stepNumberStyle}>{index + 1}</div>
        <div>
          <Typography.Text strong>{step.title}</Typography.Text>
          <Typography.Paragraph
            style={{ fontSize: 13, marginBottom: 4, marginTop: 2 }}
            type="secondary"
          >
            {step.description}
          </Typography.Paragraph>
          {step.path ? (
            <Link onClick={onClose} style={{ fontSize: 13 }} to={step.path}>
              <span style={stepLinkIconStyle}>{step.icon}</span>
              {step.linkText} <ArrowRightOutlined />
            </Link>
          ) : null}
          {step.href ? (
            <a href={step.href} style={{ fontSize: 13 }}>
              {step.linkText} <ArrowRightOutlined />
            </a>
          ) : null}
        </div>
      </div>
    ))}
  </Modal>
);

export default ClientSetupGuideModal;
